import {
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassInput,
  GradientMesh,
} from '@merritrac/shared/components';
import { KeyRound, Phone, User } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { usePortalAuth } from '../hooks/usePortalAuth.js';

import type { ReactElement } from 'react';

type LoginMode = 'manager' | 'resident';

export function LoginPage(): ReactElement {
  const [mode, setMode] = useState<LoginMode>('manager');

  // PM fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // OTP fields
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const { login, sendCode, verifyCode, error } = usePortalAuth();
  const navigate = useNavigate();

  async function handleManagerLogin(e: React.SyntheticEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(username, password);
    setSubmitting(false);
    if (ok) {
      void navigate('/');
    }
  }

  async function handleSendCode(): Promise<void> {
    setSubmitting(true);
    const ok = await sendCode(phone);
    setSubmitting(false);
    if (ok) {
      setCodeSent(true);
    }
  }

  async function handleVerifyCode(e: React.SyntheticEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    const ok = await verifyCode(phone, otpCode);
    setSubmitting(false);
    if (ok) {
      void navigate('/');
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4 relative overflow-hidden">
      <GradientMesh />
      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/20 mb-3">
            <svg
              viewBox="0 0 32 32"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              className="w-7 h-7 text-primary"
            >
              <line x1="16" y1="4" x2="16" y2="28" />
              <line x1="4" y1="16" x2="28" y2="16" />
              <line x1="7" y1="7" x2="25" y2="25" />
              <line x1="25" y1="7" x2="7" y2="25" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-text">MerriTrac</h1>
          <p className="text-sm text-text-secondary mt-1">Customer Portal</p>
        </div>

        {/* Login mode toggle */}
        <div className="flex mb-4 rounded-lg overflow-hidden border border-border/30">
          <button
            type="button"
            onClick={(): void => {
              setMode('manager');
              setCodeSent(false);
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
              mode === 'manager'
                ? 'bg-primary/20 text-primary'
                : 'bg-surface/50 text-text-secondary hover:text-text'
            }`}
          >
            <User size={14} />
            Property Manager
          </button>
          <button
            type="button"
            onClick={(): void => {
              setMode('resident');
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
              mode === 'resident'
                ? 'bg-primary/20 text-primary'
                : 'bg-surface/50 text-text-secondary hover:text-text'
            }`}
          >
            <Phone size={14} />
            Resident
          </button>
        </div>

        <GlassCard glow>
          <GlassCardContent className="pt-6 pb-6">
            {mode === 'manager' ? (
              <form
                onSubmit={(e): void => {
                  void handleManagerLogin(e);
                }}
                className="space-y-4"
              >
                <GlassInput
                  label="Username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    setUsername(e.target.value);
                  }}
                  required
                  autoComplete="username"
                />

                <GlassInput
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    setPassword(e.target.value);
                  }}
                  required
                  autoComplete="current-password"
                />

                {error !== null && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-danger-soft text-danger-fg text-sm">
                    <KeyRound size={14} />
                    <span>{error}</span>
                  </div>
                )}

                <GlassButton type="submit" className="w-full" disabled={submitting}>
                  {submitting ? 'Signing in...' : 'Sign In'}
                </GlassButton>
              </form>
            ) : (
              <>
                {!codeSent ? (
                  <div className="space-y-4">
                    <p className="text-sm text-text-secondary">
                      Enter the phone number on file for your unit to receive a verification code.
                    </p>

                    <GlassInput
                      label="Phone Number"
                      type="tel"
                      placeholder="305-555-0100"
                      value={phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        setPhone(e.target.value);
                      }}
                      required
                      autoComplete="tel"
                    />

                    {error !== null && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-danger-soft text-danger-fg text-sm">
                        <KeyRound size={14} />
                        <span>{error}</span>
                      </div>
                    )}

                    <GlassButton
                      type="button"
                      className="w-full"
                      disabled={submitting || phone.trim() === ''}
                      onClick={(): void => {
                        void handleSendCode();
                      }}
                    >
                      {submitting ? 'Sending...' : 'Send Verification Code'}
                    </GlassButton>
                  </div>
                ) : (
                  <form
                    onSubmit={(e): void => {
                      void handleVerifyCode(e);
                    }}
                    className="space-y-4"
                  >
                    <p className="text-sm text-text-secondary">
                      We sent a 6-digit code to{' '}
                      <span className="font-medium text-text">{phone}</span>.
                    </p>

                    <GlassInput
                      label="Verification Code"
                      type="text"
                      placeholder="000000"
                      value={otpCode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        setOtpCode(e.target.value);
                      }}
                      required
                      autoComplete="one-time-code"
                      maxLength={6}
                    />

                    {error !== null && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-danger-soft text-danger-fg text-sm">
                        <KeyRound size={14} />
                        <span>{error}</span>
                      </div>
                    )}

                    <GlassButton type="submit" className="w-full" disabled={submitting}>
                      {submitting ? 'Verifying...' : 'Verify & Sign In'}
                    </GlassButton>

                    <button
                      type="button"
                      onClick={(): void => {
                        setCodeSent(false);
                        setOtpCode('');
                      }}
                      className="w-full text-sm text-text-secondary hover:text-text transition-colors"
                    >
                      Use a different number
                    </button>
                  </form>
                )}
              </>
            )}
          </GlassCardContent>
        </GlassCard>

        <p className="mt-6 text-center text-xs text-text-tertiary">
          Field Service Management Platform &middot; Customer Portal
        </p>
      </div>
    </div>
  );
}
